import { useMemo, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Gift, Heart, Sparkles, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';

const WHATSAPP_NUMBER = '393472916493';
const HERO_IMAGE = '/gift-hero.png';

const benefitIcons = [Sparkles, Gift, Heart];

const GiftCard = () => {
  const { t } = useTranslation(['giftCard', 'seo']);
  const { locale } = useLocale();
  const [amount, setAmount] = useState('');

  const amounts = t('giftCard:amounts', { returnObjects: true }) as string[];
  const benefits = t('giftCard:benefits', { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  const whatsappUrl = useMemo(() => {
    const message = t('giftCard:whatsapp.message', { amount });
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
  }, [amount, t]);

  const giftCardStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Gift Card - Brace e Sapori',
    description: t('seo:giftCard.description'),
    image: `https://braceesapori.it${HERO_IMAGE}`,
    brand: {
      '@type': 'Brand',
      name: 'Brace e Sapori',
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: '35',
      highPrice: '100',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Restaurant',
        name: 'Brace e Sapori',
        url: `https://braceesapori.it${locale === 'en' ? '/en' : ''}/gift-card`,
      },
    },
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:giftCard.title')}
        description={t('seo:giftCard.description')}
        keywords={t('seo:giftCard.keywords')}
        ogImage={HERO_IMAGE}
        logicalPath="/gift-card"
        structuredData={giftCardStructuredData}
      />
      <Header />

      {/* Hero */}
      <section className="relative min-h-[80vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt={t('giftCard:hero.imageAlt')}
            className="h-full w-full object-cover object-center"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/60 to-background" />
        </div>

        <div className="relative z-10 flex min-h-[80vh] flex-col items-center justify-center px-6 py-32 text-center">
          <ScrollReveal>
            <span className="mb-6 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
              {t('giftCard:hero.label')}
            </span>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <h1 className="mb-6 text-5xl font-light uppercase tracking-[0.15em] text-foreground md:text-7xl lg:text-8xl">
              {t('giftCard:hero.title')}
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <p className="max-w-xl text-lg font-light leading-relaxed text-muted-foreground md:text-xl">
              {t('giftCard:hero.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Intro + Benefits */}
      <section className="py-24 px-6">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="mb-16 text-center">
              <h2 className="mb-6 text-4xl font-semibold text-foreground md:text-5xl">
                {t('giftCard:intro.title')}
              </h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground md:text-xl">
                {t('giftCard:intro.text')}
              </p>
            </div>
          </ScrollReveal>

          <div className="mb-20 grid gap-6 md:grid-cols-3">
            {benefits.map((benefit, index) => {
              const Icon = benefitIcons[index] ?? Gift;
              return (
                <ScrollReveal key={benefit.title} delay={100 + index * 100}>
                  <div className="h-full rounded-lg border border-border bg-card p-8 text-center">
                    <div className="mb-5 flex justify-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/20">
                        <Icon className="h-7 w-7 text-primary" aria-hidden="true" />
                      </div>
                    </div>
                    <h3 className="mb-3 text-lg font-semibold text-foreground">{benefit.title}</h3>
                    <p className="text-sm font-light leading-relaxed text-muted-foreground">
                      {benefit.text}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Selector + WhatsApp CTA */}
          <ScrollReveal delay={200}>
            <div className="mx-auto max-w-xl rounded-lg border border-primary/30 bg-card p-8 text-center md:p-12">
              <div className="mb-8 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
                  <Gift className="h-8 w-8 text-primary" aria-hidden="true" />
                </div>
              </div>

              <label
                htmlFor="gift-amount"
                className="mb-4 block text-sm font-light uppercase tracking-[0.25em] text-primary"
              >
                {t('giftCard:selector.label')}
              </label>

              <Select value={amount} onValueChange={setAmount}>
                <SelectTrigger
                  id="gift-amount"
                  className="mx-auto mb-8 h-12 max-w-xs text-base"
                  aria-label={t('giftCard:selector.label')}
                >
                  <SelectValue placeholder={t('giftCard:selector.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {amounts.map((value) => (
                    <SelectItem key={value} value={value} className="text-base">
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="hero" size="lg" className="w-full sm:w-auto" disabled={!amount} asChild={!!amount}>
                {amount ? (
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-5 w-5" aria-hidden="true" />
                    {t('giftCard:selector.cta')}
                  </a>
                ) : (
                  <span className="inline-flex items-center justify-center">
                    <MessageCircle className="mr-2 h-5 w-5" aria-hidden="true" />
                    {t('giftCard:selector.cta')}
                  </span>
                )}
              </Button>

              <p className="mt-6 text-xs font-light text-muted-foreground">
                {t('giftCard:selector.note')}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default GiftCard;
